/**
 * `<StateOverTimeRail/>` — the editorial B3.1 left-rail "STATE OVER TIME"
 * widget: a segmented bar (one flex span per state period, tinted by the
 * state's saturated `theme.stateStyle(state).dot`) over a duration legend (one
 * row per DISTINCT state — a square swatch, the state label, and the total time
 * spent in it as a mono duration).
 *
 * It is a PURE RE-SKIN view: it folds the SAME `buildStateTimeline` model the
 * B3.2 strip uses — never a new projection. The segment widths reuse the
 * model's `fromFrac`/`toFrac`; the per-state durations sum the spans' wall-clock
 * lengths. Editorial-only (the panel renders this only under the editorial
 * theme), so it always reads the editorial palette/state styles passed in.
 *
 * Brand firewall (§A9): this is *the task's* time in each factual state — the
 * work, never a person. No actor, count, rate, or ranking enters here; "Blocked"
 * is calm amber, never alarm red.
 */

import type { NodeHistoryItem } from "../api/useNodeHistory";
import type { MapTheme } from "../theme/tokens";
import type { TaskState } from "../tree/state";
import { buildStateTimeline } from "./buildStateTimeline";

export interface StateOverTimeRailProps {
  /** The focal node's events (same stream the B3.2 strip folds). */
  readonly events: NodeHistoryItem[];
  /** The resolved (editorial) theme — palette + state styles + fonts. */
  readonly theme: MapTheme;
  /** Injected `now` for deterministic tests; defaults to the current instant. */
  readonly nowISO?: string;
}

/** The four states, in lifecycle order, for a stable legend order. */
const LEGEND_ORDER: readonly TaskState[] = ["planned", "in_progress", "blocked", "done"];

/** A compact human duration ("~3d", "~4h", "~12m", "<1m") from milliseconds. */
function humanDuration(ms: number): string {
  if (ms <= 0) return "—";
  const minutes = ms / 60000;
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `~${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `~${Math.round(hours)}h`;
  const days = hours / 24;
  return `~${Math.round(days)}d`;
}

function ms(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function StateOverTimeRail({ events, theme, nowISO }: StateOverTimeRailProps) {
  const model = buildStateTimeline(events, nowISO);
  const c = theme.palette;

  // Per-state total wall-clock time across all of its spans (legend durations).
  const totals = new Map<TaskState, number>();
  for (const seg of model.segments) {
    const dur = Math.max(ms(seg.toISO) - ms(seg.fromISO), 0);
    totals.set(seg.state, (totals.get(seg.state) ?? 0) + dur);
  }

  const segments = model.segments.filter((s) => s.toFrac > s.fromFrac);

  return (
    <div data-testid="state-over-time-rail">
      <div
        className="text-[10px] uppercase"
        style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.08em" }}
      >
        State over time
      </div>

      {/* The segmented bar — one flex span per period, state-tinted. */}
      <div
        data-testid="state-over-time-bar"
        className="my-2.5 flex overflow-hidden rounded"
        style={{ height: 14, border: `1px solid ${c.rule}` }}
      >
        {segments.length === 0 ? (
          <span style={{ flex: 1, backgroundColor: c.rollupTrack }} />
        ) : (
          segments.map((seg) => (
            <span
              key={`seg-${seg.fromISO}`}
              data-testid="state-over-time-seg"
              data-state={seg.state}
              style={{
                flexGrow: Math.max(seg.toFrac - seg.fromFrac, 0.0001),
                flexBasis: 0,
                backgroundColor: theme.stateStyle(seg.state).dot ?? theme.stateStyle(seg.state).fg,
              }}
            />
          ))
        )}
      </div>

      {/* The duration legend — one row per distinct state present. */}
      <div className="text-[12px]" style={{ color: c.slate }}>
        {LEGEND_ORDER.filter((s) => totals.has(s)).map((s, i, arr) => {
          const style = theme.stateStyle(s);
          return (
            <div
              key={s}
              data-testid="state-over-time-legend-row"
              data-state={s}
              className="flex items-center gap-2 py-1.5"
              style={{ borderBottom: i === arr.length - 1 ? "none" : `1px dashed ${c.rule}` }}
            >
              <span
                aria-hidden
                className="shrink-0 rounded-sm"
                style={{ width: 11, height: 11, backgroundColor: style.dot ?? style.fg }}
              />
              <span>{style.label}</span>
              <span
                className="ml-auto text-[11px]"
                style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.04em" }}
              >
                {humanDuration(totals.get(s) ?? 0)}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-3.5 text-[11px] italic leading-relaxed" style={{ color: c.faint }}>
        The rail behind each event is tinted by the state the task was in at that moment.
      </p>
    </div>
  );
}
